import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Anchor, Plus, LogOut, Calendar, TrendingUp, Mail, Phone, Users, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

function fmt(n) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [partner, setPartner] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem("sm_partner");
    if (!raw) {
      navigate("/partners/login");
      return;
    }
    const p = JSON.parse(raw);
    setPartner(p);
    load(p.id);
  }, [navigate]);

  const load = async (pid) => {
    try {
      const res = await api.get(`/partners/${pid}/dashboard`);
      setData(res.data);
    } catch (e) {
      toast.error("Errore caricamento dashboard");
    }
  };

  const logout = () => {
    localStorage.removeItem("sm_partner");
    navigate("/");
  };

  if (!partner || !data) return <div className="min-h-screen pt-32 text-center text-ink">Loading…</div>;

  const k = data.kpi;
  const kpis = [
    { label: t("dashboard.kpiWeeks"), value: k.available_weeks, icon: Calendar },
    { label: t("dashboard.kpiRequests"), value: k.requests_received, icon: Users },
    { label: t("dashboard.kpiQuotes"), value: k.quotes_sent, icon: Mail },
    { label: t("dashboard.kpiBookings"), value: k.bookings_generated, icon: TrendingUp },
    { label: t("dashboard.kpiPotential"), value: fmt(k.potential_value), icon: Anchor, span: 2 },
  ];

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#FAFAF9] pt-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("dashboard.welcome")}</p>
            <h1 className="mt-2 font-sans text-3xl text-navy font-medium tracking-tight">{partner.company_name}</h1>
            <p className="mt-1 text-sm text-ink">{partner.contact_name} · {partner.email}</p>
          </div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            onClick={logout}
            className="border-navy/20 text-navy hover:bg-sand"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="overview" className="mt-10">
          <TabsList data-testid="dashboard-tabs" className="bg-white border border-[#E2E8F0] p-1 h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">{t("dashboard.overview")}</TabsTrigger>
            <TabsTrigger value="fleet" data-testid="tab-fleet">{t("dashboard.fleet")}</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">{t("dashboard.requests")}</TabsTrigger>
            <TabsTrigger value="lastmin" data-testid="tab-lastmin">{t("dashboard.lastMin")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {kpis.map((kpi, i) => (
                <div
                  key={i}
                  data-testid={`kpi-${i}`}
                  className={`bg-white border border-[#E2E8F0] rounded-xl p-5 ${kpi.span === 2 ? "col-span-2" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <kpi.icon className="h-4 w-4 text-turquoise" />
                    <span className="text-[11px] uppercase tracking-wider text-ink">{kpi.label}</span>
                  </div>
                  <p className="mt-3 font-serif text-3xl text-navy">{kpi.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-5">
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
                <h3 className="font-sans text-lg font-medium text-navy">{lang === "it" ? "Settimane disponibili" : "Available weeks"}</h3>
                <div className="mt-4 space-y-3">
                  {data.fleet.slice(0, 5).map((o) => (
                    <div key={o.id} className="flex items-center justify-between border-b border-[#E2E8F0] pb-3 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-navy">{o.name}</p>
                        <p className="text-xs text-ink">{o.destination} · {o.start_date} → {o.end_date}</p>
                      </div>
                      <Badge variant="outline" className="border-turquoise/40 text-turquoise">{fmt(o.total_price)}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
                <h3 className="font-sans text-lg font-medium text-navy">{lang === "it" ? "Ultimi lead" : "Latest leads"}</h3>
                <div className="mt-4 space-y-3">
                  {data.compatible_leads.slice(0, 5).map((l, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-[#E2E8F0] pb-3 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-navy">{l.lead.name} · {l.lead.num_participants}p · {fmt(l.lead.budget)}</p>
                        <p className="text-xs text-ink truncate">{l.lead.destinations.join(" / ") || "—"}</p>
                      </div>
                      <Badge className="bg-navy text-white">{l.match_score}%</Badge>
                    </div>
                  ))}
                  {data.compatible_leads.length === 0 && (
                    <p className="text-sm text-ink">{t("dashboard.noLeads")}</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="fleet" className="mt-8">
            <div className="flex justify-end mb-4">
              <AddBoatDialog partnerId={partner.id} onAdded={() => load(partner.id)} />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.fleet.map((o) => (
                <div key={o.id} data-testid={`fleet-${o.id}`} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                  <img src={o.image_url} alt={o.name} className="h-40 w-full object-cover" />
                  <div className="p-5">
                    <div className="flex items-baseline justify-between">
                      <p className="font-medium text-navy">{o.name}</p>
                      <span className="text-xs uppercase text-ink">{o.boat_type}</span>
                    </div>
                    <p className="mt-1 text-xs text-ink flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {o.destination} · {o.area}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-ink">{o.start_date} → {o.end_date}</span>
                      <span className="font-semibold text-navy">{fmt(o.total_price)}</span>
                    </div>
                    {o.last_minute_discount > 0 && (
                      <Badge className="mt-3 bg-gold text-navy">-{o.last_minute_discount}% LAST MINUTE</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-8">
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-sand text-left">
                  <tr>
                    <th className="p-4 text-xs uppercase tracking-wider text-ink">Lead</th>
                    <th className="p-4 text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Persone" : "People"}</th>
                    <th className="p-4 text-xs uppercase tracking-wider text-ink">Budget</th>
                    <th className="p-4 text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Mete" : "Destinations"}</th>
                    <th className="p-4 text-xs uppercase tracking-wider text-ink">Match</th>
                    <th className="p-4 text-xs uppercase tracking-wider text-ink">{lang === "it" ? "Barca consigliata" : "Suggested boat"}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.compatible_leads.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-ink">{t("dashboard.noLeads")}</td>
                    </tr>
                  )}
                  {data.compatible_leads.map((l, i) => (
                    <tr key={i} data-testid={`lead-row-${i}`} className="border-t border-[#E2E8F0] hover:bg-sand/40">
                      <td className="p-4">
                        <p className="font-medium text-navy">{l.lead.name}</p>
                        <p className="text-xs text-ink flex items-center gap-1"><Mail className="h-3 w-3" />{l.lead.email}</p>
                      </td>
                      <td className="p-4 text-navy">{l.lead.num_participants}</td>
                      <td className="p-4 text-navy">{fmt(l.lead.budget)}</td>
                      <td className="p-4 text-ink text-xs">{l.lead.destinations.join(" / ") || "—"}</td>
                      <td className="p-4"><Badge className="bg-navy text-white">{l.match_score}%</Badge></td>
                      <td className="p-4 text-ink text-xs">{l.matched_offer.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="lastmin" className="mt-8">
            <LastMinutePanel partnerId={partner.id} fleet={data.fleet} onUpdate={() => load(partner.id)} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AddBoatDialog({ partnerId, onAdded }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [b, setB] = useState({
    name: "",
    boat_type: "sailboat",
    destination: "Greece",
    area: "",
    beds: 6,
    cabins: 3,
    total_price: 3000,
    start_date: "",
    end_date: "",
    skipper_included: false,
    image_url: "",
    description: "",
  });

  const save = async () => {
    try {
      await api.post(`/partners/${partnerId}/boats`, b);
      toast.success(lang === "it" ? "Barca aggiunta" : "Boat added");
      setOpen(false);
      onAdded?.();
    } catch (e) {
      toast.error("Errore");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-boat-btn" className="bg-navy text-white hover:bg-[#06101f]"><Plus className="h-4 w-4 mr-1.5" /> {t("dashboard.addBoat")}</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-boat-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("dashboard.addBoat")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Field2 label="Nome" testId="b-name" value={b.name} onChange={(v) => setB({ ...b, name: v })} />
          <Field2 label="Area" testId="b-area" value={b.area} onChange={(v) => setB({ ...b, area: v })} />
          <Field2 label={lang === "it" ? "Destinazione" : "Destination"} testId="b-dest" value={b.destination} onChange={(v) => setB({ ...b, destination: v })} />
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink">Tipo</Label>
            <select
              data-testid="b-type"
              value={b.boat_type}
              onChange={(e) => setB({ ...b, boat_type: e.target.value })}
              className="mt-1 w-full h-11 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="sailboat">Sailboat</option>
              <option value="catamaran">Catamaran</option>
              <option value="yacht">Yacht</option>
              <option value="motorboat">Motorboat</option>
            </select>
          </div>
          <Field2 label="Posti letto" testId="b-beds" type="number" value={b.beds} onChange={(v) => setB({ ...b, beds: parseInt(v || "0") })} />
          <Field2 label="Cabine" testId="b-cabins" type="number" value={b.cabins} onChange={(v) => setB({ ...b, cabins: parseInt(v || "0") })} />
          <Field2 label="Prezzo totale" testId="b-price" type="number" value={b.total_price} onChange={(v) => setB({ ...b, total_price: parseFloat(v || "0") })} />
          <Field2 label="Start" testId="b-start" type="date" value={b.start_date} onChange={(v) => setB({ ...b, start_date: v })} />
          <Field2 label="End" testId="b-end" type="date" value={b.end_date} onChange={(v) => setB({ ...b, end_date: v })} />
          <Field2 label="Image URL" testId="b-image" value={b.image_url} onChange={(v) => setB({ ...b, image_url: v })} />
        </div>
        <Button data-testid="b-save" onClick={save} className="mt-4 bg-navy text-white hover:bg-[#06101f]">
          Save
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function LastMinutePanel({ partnerId, fleet, onUpdate }) {
  const { lang } = useI18n();
  const [discount, setDiscount] = useState({});

  const apply = async (offerId, d) => {
    try {
      await api.post(`/partners/${partnerId}/last-minute`, { offer_id: offerId, discount: d });
      toast.success(lang === "it" ? "Offerta last minute creata" : "Last-minute offer created");
      onUpdate?.();
    } catch (e) {
      toast.error("Errore");
    }
  };

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {fleet.map((o) => (
        <div key={o.id} data-testid={`lm-${o.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <p className="font-medium text-navy">{o.name}</p>
          <p className="text-xs text-ink">{o.destination} · {o.start_date}</p>
          <div className="mt-4 flex items-center gap-2">
            <Input
              data-testid={`lm-discount-${o.id}`}
              type="number"
              placeholder="%"
              value={discount[o.id] || o.last_minute_discount || ""}
              onChange={(e) => setDiscount({ ...discount, [o.id]: parseInt(e.target.value || "0") })}
              className="h-10 w-20"
            />
            <Button
              data-testid={`lm-apply-${o.id}`}
              size="sm"
              onClick={() => apply(o.id, discount[o.id] || o.last_minute_discount || 15)}
              className="bg-gold text-navy hover:bg-[#c89e29]"
            >
              {lang === "it" ? "Applica" : "Apply"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Field2({ label, testId, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-11" />
    </div>
  );
}
